import React, { createContext, useContext, useState, useCallback } from 'react';

// Create Context
const ActionHistoryContext = createContext();

// Custom hook to use the context
export const useActionHistory = () => {
  const context = useContext(ActionHistoryContext);
  if (!context) {
    throw new Error('useActionHistory must be used within ActionHistoryProvider');
  }
  return context;
};

// Provider component
export const ActionHistoryProvider = ({ children }) => {
  const [actions, setActions] = useState([]);

  // Add an action to history
  const pushAction = useCallback((action) => {
    setActions((prev) => [...prev, action]);
  }, []); // Empty dependency array - function is stable

  // Undo last action
  const undoLastAction = useCallback(() => {
    if (actions.length === 0) return false;
    
    const lastAction = actions[actions.length - 1];
    
    // Execute the undo function
    if (lastAction && typeof lastAction.undo === 'function') {
      lastAction.undo();
    }
    
    // Remove the action from history
    setActions((prev) => prev.slice(0, -1));
    
    return true;
  }, [actions]);

  // Clear all actions
  const clearActions = useCallback(() => {
    setActions([]);
  }, []);

  // Check if there are actions to undo
  const hasActions = actions.length > 0;

  const value = {
    actions,
    pushAction,
    undoLastAction,
    clearActions,
    hasActions,
  };

  return (
    <ActionHistoryContext.Provider value={value}>
      {children}
    </ActionHistoryContext.Provider>
  );
};
